import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import { t } from 'i18next';
import { useState } from 'react';

import PricingEventsTimeline from '~components/Job/LoadPricing/PricingEventsTimeline';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import PricingHistoryPanel from '~components/PricingHistory/PricingHistoryPanel';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { Nullable } from '~types/Nullable';

// Which host renders a Rates History trigger — the `surface` property on
// `rates_history_viewed` events. Required on every trigger so the compiler
// forces each current and future host to name itself.
export type RatesHistoryAnalyticsSurface =
  | 'project_form'
  | 'project_list'
  | 'order_form'
  | 'dispatch_rates_column'
  | 'edit_rates_dialog'
  | 'job_edit_form'
  | 'ticket_drawer';

// Mirrors the grain precedence the timeline itself uses (jobId > orderId > loadId).
function resolveHistoryScope(
  jobId?: Nullable<string>,
  orderId?: Nullable<string>,
): 'job' | 'order' | 'load' {
  if (jobId) return 'job';
  if (orderId) return 'order';
  return 'load';
}

interface PricingHistoryButtonProps {
  /** Names the object in the panel header, e.g. "Job · TRD-85699". */
  subtitle: string;
  /** Button text. Defaults to "Rates History"; pass e.g. "History" where the context is clear. */
  label?: string;
  /**
   * 'text' (default) is the orange header action. 'outlined' matches the drawer's
   * Edit/Approve/Flag buttons — use for a tertiary action alongside them.
   */
  buttonVariant?: 'text' | 'outlined';
  /** Job-grain history — the Job plus its JobAssignments and Loads. */
  jobId?: Nullable<string>;
  /** Order-grain history — the whole Order subtree. `jobId` takes precedence. */
  orderId?: Nullable<string>;
  /** Load-grain history — a single Load's events. */
  loadId?: Nullable<string>;
  analyticsSurface: RatesHistoryAnalyticsSurface;
}

/**
 * Labeled trigger for a modal/drawer header (Edit Rates modal, Order form) that opens the
 * rate-history panel for a single object. Icon + "Rates History" label, since a bare icon in a
 * header isn't self-explanatory.
 */
export default function PricingHistoryButton({
  subtitle,
  label,
  buttonVariant = 'text',
  jobId,
  orderId,
  loadId,
  analyticsSurface,
}: PricingHistoryButtonProps) {
  const isOutlined = buttonVariant === 'outlined';
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  return (
    <>
      <SmallButton
        color={isOutlined ? 'inherit' : 'brandV2OrangeDark'}
        onClick={(event) => {
          event.stopPropagation();
          AnalyticsController.track('rates_history_viewed', {
            scope: resolveHistoryScope(jobId, orderId),
            surface: analyticsSurface,
          });
          setIsPanelOpen(true);
        }}
        startIcon={<HistoryOutlined sx={{ fontSize: '18px' }} />}
        sx={
          isOutlined
            ? (theme) => ({
                bgcolor: theme.semantic.surfaceRaised,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              })
            : undefined
        }
        variant={isOutlined ? 'outlined' : 'text'}
      >
        {label ?? t('loads.pricing.pricing_history')}
      </SmallButton>

      <PricingHistoryPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        subtitle={subtitle}
        title={t('loads.pricing.pricing_history')}
      >
        <PricingEventsTimeline
          jobId={jobId}
          orderId={orderId}
          subjectId={loadId}
          subjectType={loadId ? 'Load' : undefined}
        />
      </PricingHistoryPanel>
    </>
  );
}
